// /catalog/types/new  → editor de tipo nuevo
// /catalog/types/:typeId → editor de tipo existente.
//
// Reglas:
//   - El `code` se setea solo al crear; en edicion es readonly (UNIQUE en DB).
//   - `favorite_type_id` se deriva del code (`ftype_<code>`) en crear.
//   - El form_schema vive en `details.form_schema` (jsonb), sin migration.

import { useMemo, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AdminApiError,
  createFavoriteType,
  listFavoriteTypes,
  patchFavoriteType,
  type FavoriteType,
  type FormSchema,
} from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import { useCatalogContext } from '@/components/CatalogShell';
import FormSchemaEditor, { readFormSchema, validateFormSchema } from '@/components/FormSchemaEditor';
import { fieldCls } from '@/lib/form-styles';
import { canWrite as canWriteSection } from '@/lib/access';

interface FormState {
  code: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  schema: FormSchema;
}

function initialState(): FormState {
  return { code: '', name: '', description: '', icon: '', color: '', schema: { fields: [] } };
}

function fromType(type: FavoriteType): FormState {
  const details = type.details ?? {};
  return {
    code: type.code,
    name: type.name,
    description: type.description ?? '',
    icon: typeof details['icon'] === 'string' ? (details['icon'] as string) : '',
    color: typeof details['color'] === 'string' ? (details['color'] as string) : '',
    schema: readFormSchema(details),
  };
}

function buildDetails(state: FormState, base: Record<string, unknown> = {}): Record<string, unknown> {
  const next: Record<string, unknown> = { ...base };
  if (state.icon.trim()) next['icon'] = state.icon.trim();
  else delete next['icon'];
  if (state.color.trim()) next['color'] = state.color.trim();
  else delete next['color'];
  next['form_schema'] = state.schema;
  return next;
}

export default function CatalogTypeEdit(): JSX.Element {
  const { typeId } = useParams<{ typeId: string }>();
  const isNew = !typeId || typeId === 'new';
  const { partnerId } = useCatalogContext();
  const session = useSession();
  const canWrite = canWriteSection(session, 'catalog');
  const navigate = useNavigate();
  const qc = useQueryClient();

  // Reuso la lista para evitar un endpoint de "get type by id" — el detalle
  // que necesitamos ya viene en el listado.
  const typesQ = useQuery({
    queryKey: ['admin-favorite-types', partnerId],
    queryFn: () => listFavoriteTypes(partnerId),
    enabled: !!partnerId,
  });

  const existing = useMemo(
    () => (isNew ? null : typesQ.data?.find((t) => t.favorite_type_id === typeId) ?? null),
    [isNew, typesQ.data, typeId],
  );

  const [form, setForm] = useState<FormState>(initialState);
  const [hydrated, setHydrated] = useState(false);

  // Hidratar al primer load cuando llega el data.
  if (!isNew && existing && !hydrated) {
    setForm(fromType(existing));
    setHydrated(true);
  }

  const [formError, setFormError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: async () => {
      const trimmedCode = form.code.trim();
      const id = `ftype_${trimmedCode}`;
      await createFavoriteType(partnerId, {
        favorite_type_id: id,
        code: trimmedCode,
        name: form.name.trim(),
        description: form.description.trim() || null,
        details: buildDetails(form),
      });
      return id;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ['admin-favorite-types', partnerId] });
      navigate(`/catalog/types/${encodeURIComponent(id)}`);
    },
    onError: (e) =>
      setFormError(e instanceof AdminApiError ? e.message : 'No pudimos crear el tipo.'),
  });

  const patchMut = useMutation({
    mutationFn: async () => {
      if (!existing) throw new Error('not loaded');
      await patchFavoriteType(existing.favorite_type_id, {
        name: form.name.trim(),
        description: form.description.trim() || null,
        details: buildDetails(form, existing.details),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-favorite-types', partnerId] }),
    onError: (e) =>
      setFormError(e instanceof AdminApiError ? e.message : 'No pudimos guardar el tipo.'),
  });

  function onSubmit(e: React.FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setFormError(null);
    if (!form.name.trim()) {
      setFormError('El nombre es obligatorio.');
      return;
    }
    if (isNew) {
      if (!/^[a-z0-9_-]+$/.test(form.code)) {
        setFormError('Code invalido. Usa solo a-z, 0-9, _ o -.');
        return;
      }
    }
    const schemaError = validateFormSchema(form.schema);
    if (schemaError) {
      setFormError(schemaError);
      return;
    }
    if (isNew) createMut.mutate();
    else patchMut.mutate();
  }

  if (typesQ.isLoading) return <p className="text-sm text-ink-muted">Cargando…</p>;
  if (!isNew && !existing && typesQ.data) {
    return (
      <div role="alert" className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error">
        Tipo no encontrado.{' '}
        <Link to="/catalog/types" className="underline">Volver</Link>
      </div>
    );
  }

  const isPending = createMut.isPending || patchMut.isPending;

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">{isNew ? 'Nuevo tipo' : `Editar: ${existing?.name}`}</h2>
          {!isNew && existing ? (
            <p className="text-xs font-mono text-ink-muted">{existing.favorite_type_id}</p>
          ) : null}
        </div>
        <Link to="/catalog/types" className="inline-flex items-center gap-1 text-sm text-ink-muted hover:underline">
          <ArrowLeft size={14} aria-hidden="true" />
          Volver a la lista
        </Link>
      </header>

      <section className="bg-surface dark:bg-surface-dark rounded-xl border border-surface-border dark:border-surface-dark-border p-5 space-y-4">
        <h3 className="text-sm font-semibold">Metadata</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-sm space-y-1">
            <span className="font-medium">Code <span className="text-ink-muted">(slug)</span></span>
            <input
              type="text"
              value={form.code}
              onChange={(e) =>
                setForm({ ...form, code: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') })
              }
              disabled={!isNew || !canWrite}
              required
              maxLength={32}
              placeholder="team"
              className={fieldCls({ size: 'lg', mono: true })}
            />
            {!isNew ? (
              <span className="text-xs text-ink-muted">No editable. Crear un tipo nuevo para renombrar.</span>
            ) : (
              <span className="text-xs text-ink-muted">
                Se guardara como <code>ftype_{form.code || '...'}</code>
              </span>
            )}
          </label>
          <label className="text-sm space-y-1">
            <span className="font-medium">Nombre</span>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              disabled={!canWrite}
              required
              maxLength={100}
              placeholder="Equipo"
              className={fieldCls({ size: 'lg' })}
            />
          </label>
          <label className="text-sm space-y-1 md:col-span-2">
            <span className="font-medium">Descripcion <span className="text-ink-muted">(opcional)</span></span>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              disabled={!canWrite}
              maxLength={500}
              className={fieldCls({ size: 'lg' })}
            />
          </label>
          <label className="text-sm space-y-1">
            <span className="font-medium">Icono <span className="text-ink-muted">(opcional)</span></span>
            <input
              type="text"
              value={form.icon}
              onChange={(e) => setForm({ ...form, icon: e.target.value })}
              disabled={!canWrite}
              maxLength={50}
              placeholder="users, shield, ..."
              className={fieldCls({ size: 'lg' })}
            />
          </label>
          <label className="text-sm space-y-1">
            <span className="font-medium">Color <span className="text-ink-muted">(opcional)</span></span>
            <input
              type="text"
              value={form.color}
              onChange={(e) => setForm({ ...form, color: e.target.value })}
              disabled={!canWrite}
              maxLength={20}
              placeholder="#1e88e5"
              className={fieldCls({ size: 'lg' })}
            />
          </label>
        </div>
      </section>

      <section className="bg-surface dark:bg-surface-dark rounded-xl border border-surface-border dark:border-surface-dark-border p-5 space-y-4">
        <div>
          <h3 className="text-sm font-semibold">Campos del formulario</h3>
          <p className="text-xs text-ink-muted mt-1">
            Definen los datos custom que se piden al cargar un item de este tipo.
            Si modificas un schema con items existentes, los valores viejos se
            preservan como "campos no reconocidos" para evitar perdida de data.
          </p>
        </div>
        <FormSchemaEditor
          value={form.schema}
          onChange={(schema) => setForm({ ...form, schema })}
          disabled={!canWrite}
        />
      </section>

      {formError ? (
        <div role="alert" className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error">
          {formError}
        </div>
      ) : null}

      <div className="flex justify-end gap-2">
        <Link
          to="/catalog/types"
          className="min-h-[2.75rem] inline-flex items-center rounded-lg border border-surface-border dark:border-surface-dark-border px-4 text-sm"
        >
          Cancelar
        </Link>
        <button
          type="submit"
          disabled={!canWrite || isPending}
          className="min-h-[2.75rem] rounded-lg bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 disabled:opacity-50"
        >
          {isPending ? 'Guardando…' : isNew ? 'Crear tipo' : 'Guardar cambios'}
        </button>
      </div>
    </form>
  );
}
